// components/layouting/common/FileUploadModal.jsx
import React, { useState } from "react";
import { Modal, Button, Nav } from "react-bootstrap";
import api from "../../../services/api";

export default function FileUploadModal({ show, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [jobName, setJobName] = useState("");
  const [activeTab, setActiveTab] = useState("laser"); // "laser" или "plotter"
  
  // Лазер
  const [autoMarks, setAutoMarks] = useState(true);
  const [markCountH, setMarkCountH] = useState(4);
  const [markCountV, setMarkCountV] = useState(3);
  const [laserWidth, setLaserWidth] = useState(825);
  const [laserHeight, setLaserHeight] = useState(510);
  
  // Плоттер
  const [plotterMaxWidth, setPlotterMaxWidth] = useState(1070);
  const [plotterMaxHeight, setPlotterMaxHeight] = useState(1500);
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f);
    if (f?.name) {
      setJobName(f.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const reset = () => {
    setFile(null);
    setJobName("");
    setActiveTab("laser");
    setAutoMarks(true);
    setMarkCountH(4);
    setMarkCountV(3);
    setLaserWidth(825);
    setLaserHeight(510);
    setPlotterMaxWidth(1070);
    setPlotterMaxHeight(1500);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setIsUploading(true);

    const form = new FormData();
    form.append("file", file);

    let qs = `?name=${encodeURIComponent(jobName || file.name)}`;

    if (activeTab === "laser") {
      qs += `&width=${laserWidth}&height=${laserHeight}`;
      if (autoMarks) {
        qs += `&markCountV=${markCountV}&markCountH=${markCountH}`;
      }
      qs += `&cutType=1`;
    } else {
      qs += `&width=${plotterMaxWidth}&height=${plotterMaxHeight}&cutType=2`;
    }

    try {
      await api.post(`/layouting/sheets/upload${qs}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      });

      reset();
      onUploaded && onUploaded();
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => { reset(); onClose(); }} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Загрузить скомпонованный лист</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link 
              active={activeTab === "laser"} 
              onClick={() => setActiveTab("laser")}
            >
              Лазер
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === "plotter"} 
              onClick={() => setActiveTab("plotter")}
            >
              Плоттер
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <div className="mb-3">
          <label className="form-label">Название</label>
          <input 
            type="text" 
            className="form-control" 
            value={jobName} 
            onChange={(e) => setJobName(e.target.value)} 
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Файл CDR</label>
          <input 
            type="file" 
            accept=".cdr" 
            className="form-control" 
            onChange={handleFileChange} 
          />
        </div>

        {activeTab === "laser" && (
          <>
            <div className="form-check form-switch mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="autoMarksSwitch"
                checked={autoMarks}
                onChange={(e) => setAutoMarks(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="autoMarksSwitch">
                Автоматически проставлять метки
              </label>
            </div>
            
            {autoMarks && (
              <div>
                <div className="row g-2 mb-3">
                  <div className="col">
                    <label className="form-label">Кол-во меток горизонтально</label>
                    <select 
                      className="form-select" 
                      value={markCountH} 
                      onChange={(e) => setMarkCountH(parseInt(e.target.value))}
                    >
                      {[...Array(9)].map((_, i) => (
                        <option key={i+2} value={i+2}>{i+2}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label">Кол-во меток вертикально</label>
                    <select 
                      className="form-select" 
                      value={markCountV} 
                      onChange={(e) => setMarkCountV(parseInt(e.target.value))}
                    >
                      {[...Array(9)].map((_, i) => (
                        <option key={i+2} value={i+2}>{i+2}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col">
                    <label className="form-label">Ширина листа (мм)</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={laserWidth} 
                      onChange={(e) => setLaserWidth(parseInt(e.target.value || 0))} 
                    />
                  </div>
                  <div className="col">
                    <label className="form-label">Высота листа (мм)</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={laserHeight} 
                      onChange={(e) => setLaserHeight(parseInt(e.target.value || 0))} 
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "plotter" && (
          <div className="row g-2">
            <div className="col">
              <label className="form-label">Максимальная ширина (мм)</label>
              <input 
                type="number" 
                className="form-control" 
                value={plotterMaxWidth} 
                onChange={(e) => setPlotterMaxWidth(parseInt(e.target.value || 0))} 
              />
            </div>
            <div className="col">
              <label className="form-label">Максимальная высота (мм)</label>
              <input 
                type="number" 
                className="form-control" 
                value={plotterMaxHeight} 
                onChange={(e) => setPlotterMaxHeight(parseInt(e.target.value || 0))} 
              />
            </div>
          </div>
        )}
        
        {uploadProgress > 0 && (
          <div className="mt-3">
            <div className="progress">
              <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
                {uploadProgress}%
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={() => { reset(); onClose(); }} 
          disabled={isUploading}
        >
          Отмена
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          disabled={!file || isUploading}
        >
          {isUploading ? "Загрузка..." : "Отправить"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}